import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import dayjs from "dayjs";
import { Calendar, Mail, Phone, Star } from "lucide-react";
import { Badge } from "@/components/catalyst/badge";
import { WorkOrderAssignmentStatus, PayoutStatus } from "@/gql/graphql";
import { useNavigate } from "@tanstack/react-router";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { useUpdateAssignmentStatus } from "@/app/ProDrivers/WorkOrders/Hooks/useUpdateAssignmentStatus";
import {
  ExclamationTriangleIcon,
  PencilIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { Avatar } from "@/components/catalyst/avatar";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useWorkOrderAssignmentActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignmentActions";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { UpdateWorkOrderAssignmentDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/UpdateWorkOrderAssignmentDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UpdateProDriverDialog } from "@/app/ProDrivers/Drivers/Components/Dialogs/UpdateProDriverDialog";
import { ProDriverReviewDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProDriverReviewDialog";
import { AddPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/AddPayoutDialog";
import { ProcessDriverPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProcessDriverPayoutDialog";
import { ApproveReceiptsDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ApproveReceiptsDialog";
import { useTranslations } from "use-intl";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { getInitials } from "@/lib/Utils/getInitials";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import { WorkOrderRecord } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { AssignmentStatusBadge } from "@/app/ProDrivers/WorkOrders/Components/AssignmentStatusBadge";
import { assignmentStatusColorMap } from "@/app/ProDrivers/WorkOrders/utils/assignmentStatusColorMap";
import {
  AssignmentFilesPreview,
  useAssignmentFiles,
} from "@/app/ProDrivers/WorkOrders/Components/Cards/AssignmentFilesPreview";
import { AgreementStatusBadge } from "@/app/ProDrivers/Agreements/Components/AgreementStatusBadge";
import { AgreementDialog } from "@/app/ProDrivers/Agreements/Components/Dialogs/AgreementDialog";
import { CreateAgreementDialog } from "@/app/ProDrivers/Agreements/Components/Dialogs/CreateAgreementDialog";
import { ReportIncidentButton } from "@/app/Incidents/Components/ReportIncidentButton";

type WorkOrderAssignment = WorkOrderRecord["assignments"][number];

function formatDateWithDay(dateString: string): string {
  const date = new Date(dateString);
  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${dayOfWeek} ${formatDate(dateString)}`;
}

function calculateAge(dateOfBirth: string): number {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

interface WorkOrderDriverCardProps {
  assignment: WorkOrderAssignment;
  workOrderId: string;
  index: number;
}

export function WorkOrderDriverCard({
  assignment,
  workOrderId,
  index,
}: WorkOrderDriverCardProps) {
  const tDrivers = useTranslations("workOrder.drivers");
  const t = useTranslations("workOrder.cards.driver");
  const tStatus = useTranslations("workOrder.assignment.status");
  const { open: openUpdateDialog } = useDialog(UpdateWorkOrderAssignmentDialog);
  const { open: openReviewDialog } = useDialog(ProDriverReviewDialog);
  const { openAsPromise: openPayoutDialog } = useDialog(AddPayoutDialog);
  const { openAsPromise: openProcessPayoutDialog } = useDialog(
    ProcessDriverPayoutDialog,
  );
  const { openAsPromise: openApproveReceiptsDialog } = useDialog(
    ApproveReceiptsDialog,
  );
  const getActions = useWorkOrderAssignmentActions();
  const hasRating = !!assignment.proDriver.rating;
  const navigate = useNavigate();
  const { isAdmin } = useTenant();
  const { handleStatusChange, isPending: isUpdatingStatus } =
    useUpdateAssignmentStatus(assignment.id);

  const { handleUpload } = useAssignmentFiles(assignment.id);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    disabled: !isAdmin,
    noClick: true,
    noKeyboard: true,
    maxSize: 1024 * 1024 * 25,
    maxFiles: 10,
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/plain": [],
      "text/csv": [],
    },
  });

  const canApproveReceipts =
    isAdmin && assignment.status === WorkOrderAssignmentStatus.ReceiptsUploaded;
  const canMarkCompleted =
    isAdmin && assignment.status === WorkOrderAssignmentStatus.ReceiptsApproved;

  const handleApproveReceipts = async () => {
    try {
      await openApproveReceiptsDialog({
        workOrderAssignmentId: assignment.id,
      });
    } catch {
      // User cancelled the dialog
    }
  };

  const handleMarkCompleted = async () => {
    const pendingPayout = assignment.payouts?.find(
      (p) => p.status === PayoutStatus.Pending,
    );

    try {
      if (pendingPayout) {
        await openProcessPayoutDialog({ payoutId: pendingPayout.id });
      } else {
        await openPayoutDialog({
          workOrderAssignmentId: assignment.id,
        });
      }
    } catch {
      // User cancelled the dialog
    }
  };

  const openDrawer = () => {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        assignmentId: assignment.id,
      }),
    });
  };

  const rootProps = getRootProps();

  return (
    <Card
      {...rootProps}
      className={cn(
        isAdmin
          ? "cursor-pointer transition-shadow hover:shadow-md"
          : "transition-shadow",
        isDragActive && "ring-primary/60 ring-2 ring-offset-2",
        rootProps.className,
      )}
      onClick={isAdmin ? openDrawer : undefined}
    >
      <input {...getInputProps()} />
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <UserIcon className="h-4 w-4 text-gray-500" />
            <span>{t("title", { number: index + 1 })}</span>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin ? (
              <div onClick={(e) => e.stopPropagation()}>
                <AssignmentStatusBadge
                  status={assignment.status}
                  onStatusChange={handleStatusChange}
                  disabled={isUpdatingStatus}
                />
              </div>
            ) : (
              <Badge color={assignmentStatusColorMap[assignment.status]}>
                {tStatus(assignment.status)}
              </Badge>
            )}
            {isAdmin ? (
              <div onClick={(e) => e.stopPropagation()}>
                <IconMenuActions actions={getActions(assignment)} />
              </div>
            ) : null}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="mb-4 flex items-start gap-3">
          <Avatar
            initials={getInitials(assignment.proDriver.name!)}
            className="ring-border size-10 shrink-0 bg-white shadow-sm ring-1"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-gray-900">
                {assignment.proDriver.name}
              </h4>
              {assignment.proDriver.numberOfCompletedAssignments > 0 ? (
                <Badge color="zinc" className="px-1.5 py-0 text-xs">
                  {tDrivers("drivesCount", {
                    count: assignment.proDriver.numberOfCompletedAssignments,
                  })}
                </Badge>
              ) : null}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openReviewDialog({
                    proDriverId: assignment.proDriver.id,
                    workOrderAssignmentId: assignment.id,
                  });
                }}
                className={
                  hasRating
                    ? "flex items-center gap-1 rounded-sm bg-amber-50 px-1.5 text-xs text-amber-600 transition-colors hover:bg-amber-100"
                    : "text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                }
              >
                {hasRating ? (
                  <>
                    <Star className="size-3 fill-current" />
                    <span className="font-semibold">
                      {assignment.proDriver.rating?.toFixed(1)}
                    </span>
                  </>
                ) : (
                  tDrivers("reviews")
                )}
              </button>
            </div>
            {/* Contact Info */}
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              {assignment.proDriver.email ? (
                <div className="flex min-w-0 items-center gap-1.5">
                  <Mail className="size-3 shrink-0 opacity-70" />
                  <CopyToClipboard text={assignment.proDriver.email}>
                    {assignment.proDriver.email}
                  </CopyToClipboard>
                </div>
              ) : null}

              {assignment.proDriver.phone ? (
                <>
                  <span className="hidden text-gray-300 sm:inline">•</span>
                  <div className="flex items-center gap-1.5">
                    <Phone className="size-3 shrink-0 opacity-70" />
                    <CopyToClipboard text={assignment.proDriver.phone}>
                      {assignment.proDriver.phone}
                    </CopyToClipboard>
                  </div>
                </>
              ) : null}

              {assignment.proDriver.date_of_birth ? (
                <>
                  <span className="hidden text-gray-300 sm:inline">•</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="size-3 shrink-0 opacity-70" />
                        <CopyToClipboard
                          text={assignment.proDriver.date_of_birth}
                        >
                          {new Date(
                            assignment.proDriver.date_of_birth,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "2-digit",
                          })}
                        </CopyToClipboard>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("yearsOld", {
                        age: calculateAge(assignment.proDriver.date_of_birth),
                      })}
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <>
                  <span className="mr-2 hidden text-gray-300 sm:inline">•</span>
                  <MissingDateOfBirth proDriverId={assignment.proDriver.id} />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Assignment Details */}
          <AssignmentDetailsSection
            assignment={assignment}
            onEdit={
              isAdmin ? () => openUpdateDialog({ assignment }) : undefined
            }
          />

          {isAdmin ? <AssignmentFilesPreview assignment={assignment} /> : null}

          {assignment.driver_delivered_at ? (
            <ReportIncidentButton
              workOrderId={workOrderId}
              assignmentId={assignment.id}
              driverDeliveredAt={assignment.driver_delivered_at}
            />
          ) : null}

          {/* Status Change Button */}
          {canApproveReceipts ? (
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                color="green"
                className="w-full"
                disabled={isUpdatingStatus}
                onClick={handleApproveReceipts}
              >
                {t("approveReceipts")}
              </Button>
            </div>
          ) : null}

          {canMarkCompleted ? (
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                color="green"
                className="w-full"
                disabled={isUpdatingStatus}
                onClick={handleMarkCompleted}
              >
                {t("payAndComplete")}
              </Button>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function MissingDateOfBirth({ proDriverId }: { proDriverId: string }) {
  const tDrivers = useTranslations("workOrder.drivers");
  const { open } = useDialog(UpdateProDriverDialog);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        open({ paidDriverId: proDriverId });
      }}
      className="flex items-center"
    >
      <ExclamationTriangleIcon className="mr-1 h-3 w-3" />
      {tDrivers("addDateOfBirth")}
    </button>
  );
}

function AssignmentDetailsSection({
  assignment,
  onEdit,
}: {
  assignment: WorkOrderAssignment;
  onEdit?: () => void;
}) {
  const t = useTranslations("workOrder.cards.driver");
  const { isAdmin } = useTenant();
  const hasDetails =
    assignment.pickup_date ||
    assignment.delivery_date ||
    assignment.booking_reference ||
    (isAdmin && assignment.driver_notes);

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
      {hasDetails ? (
        <div className="space-y-3 text-xs">
          <div className="flex items-start justify-between gap-4">
            <div className="grid flex-1 grid-cols-4 gap-4">
              {assignment.pickup_date || assignment.pickup_time ? (
                <div>
                  <div className="text-gray-500">{t("pickup")}</div>
                  <div className="font-medium text-gray-900">
                    {assignment.pickup_time ? `${assignment.pickup_time} ` : ""}
                    {assignment.pickup_date
                      ? formatDateWithDay(assignment.pickup_date)
                      : ""}
                  </div>
                </div>
              ) : (
                <div />
              )}
              {assignment.delivery_date || assignment.delivery_time ? (
                <div>
                  <div className="text-gray-500">{t("delivery")}</div>
                  <div className="font-medium text-gray-900">
                    {assignment.delivery_time
                      ? `${assignment.delivery_time} `
                      : ""}
                    {assignment.delivery_date
                      ? formatDateWithDay(assignment.delivery_date)
                      : ""}
                  </div>
                </div>
              ) : (
                <div />
              )}
              {assignment.driver_pick_up_at ? (
                <div>
                  <div className="text-gray-500">{t("pickedUp")}</div>
                  <div className="font-medium text-gray-900">
                    {formatDateTimeLocal(assignment.driver_pick_up_at)} (UTC
                    {dayjs().format("Z").replace(/:00$/, "")})
                  </div>
                </div>
              ) : (
                <div />
              )}
              {assignment.driver_delivered_at ? (
                <div>
                  <div className="text-gray-500">{t("delivered")}</div>
                  <div className="font-medium text-gray-900">
                    {formatDateTimeLocal(assignment.driver_delivered_at)} (UTC
                    {dayjs().format("Z").replace(/:00$/, "")})
                  </div>
                </div>
              ) : (
                <div />
              )}
            </div>
            {onEdit ? (
              <Button
                plain
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <PencilIcon />
              </Button>
            ) : null}
          </div>
          {assignment.booking_reference ? (
            <div>
              <div className="text-gray-500">{t("bookingRef")}</div>
              <div className="font-medium text-gray-900">
                {assignment.booking_reference}
              </div>
            </div>
          ) : null}
          {isAdmin && assignment.driver_notes ? (
            <div className="rounded-md bg-white p-2 shadow-sm ring-1 ring-gray-200">
              <div className="text-gray-500">{t("driverNotes")}</div>
              <p className="mt-0.5 font-medium text-gray-900">
                {assignment.driver_notes}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">{t("noMovementDetails")}</p>
          {onEdit ? (
            <Button
              plain
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <PencilIcon />
            </Button>
          ) : null}
        </div>
      )}
      {isAdmin ? (
        <AgreementRow assignment={assignment} hasDetails={!!hasDetails} />
      ) : null}
    </div>
  );
}

/**
 * A drive without an agreement is a gap, not an empty field, so the missing
 * case is called out rather than left blank.
 */
function AgreementRow({
  assignment,
  hasDetails,
}: {
  assignment: WorkOrderAssignment;
  hasDetails: boolean;
}) {
  const t = useTranslations("workOrder.cards.driver");
  const { open: openAgreementDialog } = useDialog(AgreementDialog);
  const { openAsPromise: openCreateDialog } = useDialog(CreateAgreementDialog);
  const agreement = assignment.activeProDriverAgreement;

  async function handleCreate() {
    const newAgreementId = await openCreateDialog({
      workOrderAssignmentId: assignment.id,
    }).catch(() => null);

    if (newAgreementId) {
      openAgreementDialog({ agreementId: newAgreementId });
    }
  }

  return (
    <div
      className={cn(
        "text-xs",
        hasDetails && "mt-3 border-t border-gray-200 pt-3",
      )}
    >
      <div className="text-gray-500">{t("agreement")}</div>
      {agreement ? (
        <button
          type="button"
          className="mt-0.5 flex items-center gap-2 text-left"
          onClick={(e) => {
            e.stopPropagation();
            openAgreementDialog({ agreementId: agreement.id });
          }}
        >
          <span className="font-medium text-blue-600 hover:underline">
            {agreement.title}
          </span>
          <AgreementStatusBadge status={agreement.status} />
        </button>
      ) : (
        <button
          type="button"
          className="mt-0.5 flex items-center gap-1.5 font-medium text-amber-600 hover:text-amber-700 hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            handleCreate();
          }}
        >
          <ExclamationTriangleIcon className="size-3.5 shrink-0" />
          {t("noAgreement")}
        </button>
      )}
    </div>
  );
}
